import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { stateColor } from '../../../theme';
import type { ContactRequestValue } from '../types';
import { PRICE_FIELD_KEYS, recalculatePrices, type PriceFieldKey } from '../utils/recalculatePrices';

const MESSAGE_MAX_LENGTH = 500;

const priceSchema = z
    .string()
    .trim()
    .min(1, 'OFFER_REQUESTS.CONTACT.REQUEST.ERROR_REQUIRED')
    .regex(/^\d+([.]\d{1,2})?$/, 'OFFER_REQUESTS.CONTACT.REQUEST.ERROR_NUMBER'); // non-negative number, optional 1–2 decimal places

const vatRateSchema = priceSchema.refine((raw) => {
    const rate = Number(raw);
    return rate >= 0 && rate <= 10;
}, 'OFFER_REQUESTS.CONTACT.REQUEST.ERROR_VAT_RANGE');

const requestPriceSchema = z.object({
    netPrice: priceSchema,
    vatRate: vatRateSchema,
    grossPrice: priceSchema,
});

type PriceFieldErrors = Partial<Record<PriceFieldKey, string>>;

function toDecimalInput(raw: string): string {
    const cleaned = raw.replace(/[^\d.]/g, '');
    const [rawWhole, ...fractions] = cleaned.split('.');
    if (fractions.length === 0) {
        return rawWhole;
    }
    // A leading decimal point ('.5') becomes '0.5' so it passes price validation.
    const whole = rawWhole === '' ? '0' : rawWhole;
    return `${whole}.${fractions.join('').slice(0, 2)}`;
}

const TIP_KEYS = [
    'OFFER_REQUESTS.CONTACT.TIPS.ITEM_1',
    'OFFER_REQUESTS.CONTACT.TIPS.ITEM_2',
    'OFFER_REQUESTS.CONTACT.TIPS.ITEM_3',
    'OFFER_REQUESTS.CONTACT.TIPS.ITEM_4',
] as const;

const EMPTY_VALUE: ContactRequestValue = {
    netPrice: '',
    vatRate: '',
    grossPrice: '',
    message: '',
};

export type ContactRequestFormProps = {
    title: string;
    requiresPrice: boolean;
    onSubmit: (value: ContactRequestValue) => void;
    onCancel: () => void;
};

export function ContactRequestForm({
    title,
    requiresPrice,
    onSubmit,
    onCancel,
}: ContactRequestFormProps): ReactElement {
    const { t } = useTranslation();
    const [value, setValue] = useState<ContactRequestValue>(EMPTY_VALUE);
    const [priceErrors, setPriceErrors] = useState<PriceFieldErrors>({});
    const [messageError, setMessageError] = useState<string>();
    const [tipsVisible, setTipsVisible] = useState(true);

    function update<K extends keyof ContactRequestValue>(key: K, next: ContactRequestValue[K]): void {
        setValue((current) => ({ ...current, [key]: next }));
    }

    function updatePrice(key: PriceFieldKey, raw: string): void {
        const merged = { ...value, [key]: toDecimalInput(raw) };
        const computed = recalculatePrices(key, merged);
        setValue({ ...merged, ...computed });
        setPriceErrors((current) => {
            const cleared: PriceFieldErrors = { ...current, [key]: undefined };
            for (const computedKey of Object.keys(computed) as PriceFieldKey[]) {
                cleared[computedKey] = undefined;
            }
            return cleared;
        });
    }

    function handleSubmit(): void {
        if (requiresPrice) {
            const result = requestPriceSchema.safeParse(value);
            if (!result.success) {
                const fieldErrors = result.error.flatten().fieldErrors;
                setPriceErrors(
                    Object.fromEntries(PRICE_FIELD_KEYS.map((key) => [key, fieldErrors[key]?.[0]])) as PriceFieldErrors,
                );
                return;
            }
        } else if (value.message.trim() === '') {
            // The message is the only meaningful input when no price is requested.
            setMessageError('OFFER_REQUESTS.CONTACT.REQUEST.ERROR_REQUIRED');
            return;
        }
        setPriceErrors({});
        setMessageError(undefined);
        onSubmit(value);
    }

    return (
        <Box
            component="form"
            onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
            }}>
            <CardContent>
                <Stack spacing={3}>
                    <Typography variant="subtitle1" component="h3">
                        {title}
                    </Typography>

                    {requiresPrice && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                fullWidth
                                inputMode="decimal"
                                label={t('OFFER_REQUESTS.CONTACT.REQUEST.NET_PRICE')}
                                value={value.netPrice}
                                onChange={(event) => updatePrice('netPrice', event.target.value)}
                                error={priceErrors.netPrice !== undefined}
                                helperText={priceErrors.netPrice ? t(priceErrors.netPrice) : undefined}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">CHF</InputAdornment>,
                                    },
                                    // Keep the label on the border so the long text clears the adornment.
                                    inputLabel: { shrink: true },
                                }}
                            />
                            <TextField
                                fullWidth
                                inputMode="decimal"
                                label={t('OFFER_REQUESTS.CONTACT.REQUEST.VAT_RATE')}
                                value={value.vatRate}
                                onChange={(event) => updatePrice('vatRate', event.target.value)}
                                error={priceErrors.vatRate !== undefined}
                                helperText={priceErrors.vatRate ? t(priceErrors.vatRate) : undefined}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    },
                                    inputLabel: { shrink: true },
                                }}
                            />
                            <TextField
                                fullWidth
                                inputMode="decimal"
                                label={t('OFFER_REQUESTS.CONTACT.REQUEST.GROSS_PRICE')}
                                value={value.grossPrice}
                                onChange={(event) => updatePrice('grossPrice', event.target.value)}
                                error={priceErrors.grossPrice !== undefined}
                                helperText={priceErrors.grossPrice ? t(priceErrors.grossPrice) : undefined}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">CHF</InputAdornment>,
                                    },
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Stack>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label={t('OFFER_REQUESTS.CONTACT.REQUEST.DESCRIPTION_LABEL')}
                        placeholder={t('OFFER_REQUESTS.CONTACT.REQUEST.DESCRIPTION_PLACEHOLDER')}
                        value={value.message}
                        onChange={(event) => {
                            update('message', event.target.value);
                            setMessageError(undefined);
                        }}
                        error={messageError !== undefined}
                        helperText={
                            messageError
                                ? t(messageError)
                                : t('OFFER_REQUESTS.CONTACT.REQUEST.DESCRIPTION_HELPER', {
                                      count: value.message.length,
                                      max: MESSAGE_MAX_LENGTH,
                                  })
                        }
                        slotProps={{
                            htmlInput: { maxLength: MESSAGE_MAX_LENGTH },
                            inputLabel: { shrink: true },
                        }}
                    />

                    {tipsVisible && (
                        <Alert
                            severity="success"
                            onClose={() => setTipsVisible(false)}
                            sx={(theme) => ({
                                border: 1,
                                borderColor: stateColor(theme, 'outlinedBorder', 'success'),
                            })}>
                            <AlertTitle>{t('OFFER_REQUESTS.CONTACT.TIPS.TITLE')}</AlertTitle>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {TIP_KEYS.map((key) => (
                                    <li key={key}>{t(key)}</li>
                                ))}
                            </Box>
                        </Alert>
                    )}
                </Stack>
            </CardContent>

            <CardActions sx={{ p: 2, gap: 1 }}>
                <Button type="submit" variant="contained">
                    {t('OFFER_REQUESTS.CONTACT.REQUEST.SEND')}
                </Button>
                <Button variant="contained" color="inherit" onClick={onCancel}>
                    {t('OFFER_REQUESTS.CONTACT.REQUEST.CANCEL')}
                </Button>
            </CardActions>
        </Box>
    );
}
